import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface AuthMode {
  password_login: boolean;
  access: boolean;
}

export function Login() {
  const [mode, setMode] = useState<AuthMode | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    fetch('/api/auth/mode')
      .then((r) => r.json())
      .then(setMode)
      .catch(() => setMode({ password_login: false, access: false }));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const next = new URLSearchParams(location.search).get('next') || '/admin';
      nav(next, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <Activity className="h-8 w-8 text-green-500" />
          </div>
          <CardTitle>PX7 Uptime Monitor</CardTitle>
        </CardHeader>
        <CardContent>
          {mode === null ? (
            <div className="text-sm text-muted-foreground text-center">Loading…</div>
          ) : !mode.password_login ? (
            <div className="space-y-3 text-sm text-muted-foreground text-center">
              <p>
                This instance uses <strong>Cloudflare Access</strong>. Go to
                <a href="/admin" className="underline underline-offset-4 ml-1">
                  /admin
                </a>{' '}
                to sign in — you'll get an email code from Cloudflare.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-3.5 w-3.5" /> Admin password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  required
                  placeholder="••••••••"
                />
                <p className="text-xs text-muted-foreground">
                  The value you set with <code className="font-mono">wrangler secret put ADMIN_SECRET</code>.
                </p>
              </div>
              {error && (
                <div className="text-sm text-destructive rounded border border-destructive/50 bg-destructive/10 px-3 py-2">
                  {error === 'invalid_credentials' ? 'Wrong password.' : error}
                </div>
              )}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
